import React from 'react';
import QAWidgetBuilder from './QAWidgetBuilder';
import QAWidgetRenderer from './QAWidgetRenderer';
import Preview from './Preview';
import type { WidgetDefinition } from '../../../registry';
import type { QAWidgetConfig, QADataContent } from './types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/free-solid-svg-icons';

export { default as QAWidgetBuilder } from './QAWidgetBuilder';
export { default as QAWidgetRenderer } from './QAWidgetRenderer';
export * from './types';

export const definition: WidgetDefinition = {
  type: 'qa',
  meta: {
    label: 'Q&A',
    icon: React.createElement(FontAwesomeIcon, { icon: faCircleQuestion }),
    description: 'Display questions and answers from dashboard data',
    isSystem: true,
    isCreatable: false,
    shareable: true,
    editableInWorkflow: true,
    dataFlow: 'both',
    lastUpdated: '2026-04-06',
  },
  defaultSize: { w: 6, h: 5, minW: 4, minH: 4 },
  defaultConfig: () => ({
    layout: 'vertical',
    showQuestionNumbers: true,
    allowAnswerEditing: true,
  } as QAWidgetConfig),
  Builder: QAWidgetBuilder,
  Renderer: QAWidgetRenderer,
  Preview,
  validate: (data: unknown): string[] => {
    const qaData = data as QADataContent | undefined;
    if (!qaData?.items?.length) return [];
    const errors: string[] = [];
    qaData.items.forEach((item, i) => {
      const label = item.question.length > 50 ? item.question.substring(0, 50) + '...' : item.question;
      const prefix = `Question ${i + 1}: "${label}"`;
      const answer = item.answer?.trim() ?? '';

      // Only items explicitly marked required must be answered. Previously
      // every item was forced, so the `required` flag was a no-op.
      if (!answer) {
        if (item.required) errors.push(`${prefix} requires an answer`);
        return; // optional + empty: nothing else to check
      }

      // Type validation applies to any non-empty answer.
      if (item.answerType === 'number' && !Number.isFinite(Number(answer))) {
        errors.push(`${prefix} must be a number`);
      }
      if (
        item.answerType === 'select' &&
        item.options?.length &&
        !item.options.includes(answer)
      ) {
        errors.push(`${prefix} must be one of: ${item.options.join(', ')}`);
      }
    });
    return errors;
  },
};
